import { Field } from '@base-ui/react/field';
import { Fieldset } from '@base-ui/react/fieldset';
import { FileText, Upload } from 'lucide-react';
import { Autocomplete } from '@/components/ui/Autocomplete';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { LOCATION_SUGGESTIONS } from '@/consts/locations';
import { useProfileSettings } from '@/providers/settings/SettingsContext';

export const ProfileSection = () => {
  const {
    experienceYears,
    locationPreference,
    mark,
    minSalary,
    name,
    openFileDialog,
    resumeFileName,
    setExperienceYears,
    setLocationPreference,
    setMinSalary,
    setName,
    setTargetRoles,
    targetRoles,
    uploadResume,
  } = useProfileSettings();

  return (
    <Fieldset.Root>
      <Fieldset.Legend render={<SectionHeader>Profile</SectionHeader>} />
      <div className="space-y-4 rounded-lg border border-border bg-surface/60 p-4">
        <div className="grid grid-cols-2 gap-3">
          <Field.Root className="block">
            <div className="mb-1.5 flex items-baseline justify-between">
              <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                Name
              </Field.Label>
            </div>
            <Input
              onChange={(e) => mark(setName)(e.target.value)}
              size="sm"
              value={name}
            />
          </Field.Root>
          <Field.Root className="block">
            <div className="mb-1.5 flex items-baseline justify-between">
              <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                Experience
              </Field.Label>
              <Field.Description className="font-mono text-xs lg:text-sm text-subtle">
                years
              </Field.Description>
            </div>
            <Input
              font="mono"
              onChange={(e) => mark(setExperienceYears)(e.target.value)}
              size="sm"
              value={experienceYears}
            />
          </Field.Root>
          <Field.Root className="block">
            <div className="mb-1.5 flex items-baseline justify-between">
              <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                Min salary
              </Field.Label>
              <Field.Description className="font-mono text-xs lg:text-sm text-subtle">
                USD
              </Field.Description>
            </div>
            <Input
              font="mono"
              onChange={(e) => mark(setMinSalary)(e.target.value)}
              size="sm"
              value={minSalary}
            />
          </Field.Root>
          <Field.Root className="block">
            <div className="mb-1.5 flex items-baseline justify-between">
              <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                Location
              </Field.Label>
            </div>
            <Autocomplete
              items={[...LOCATION_SUGGESTIONS]}
              onValueChange={mark(setLocationPreference)}
              value={locationPreference}
            />
          </Field.Root>
        </div>
        <Field.Root className="block">
          <div className="mb-1.5 flex items-baseline justify-between">
            <Field.Label className="text-sm lg:text-base font-medium text-foreground">
              Target roles
            </Field.Label>
            <Field.Description className="font-mono text-xs lg:text-sm text-subtle">
              comma-separated
            </Field.Description>
          </div>
          <Input
            onChange={(e) => mark(setTargetRoles)(e.target.value)}
            size="sm"
            value={targetRoles}
          />
        </Field.Root>
        <Field.Root className="block" id="resume" name="resume">
          <div className="mb-1.5 flex items-baseline justify-between">
            <Field.Label className="text-sm lg:text-base font-medium text-foreground">
              Resume
            </Field.Label>
          </div>
          <div className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2">
            <div className="flex min-w-0 items-center gap-2.5">
              <FileText className="size-4 shrink-0 text-foreground-muted" />
              <span className="truncate text-sm lg:text-base text-foreground">
                {resumeFileName ?? 'No resume uploaded'}
              </span>
              <input className="sr-only" id="resume" />
            </div>
            <Button
              loading={uploadResume.isPending}
              onClick={openFileDialog}
              size="xs"
              type="button"
              variant="ghost"
            >
              <Upload className="size-3" />
              Replace
            </Button>
          </div>
          <Field.Error className="mt-1.5 font-mono text-xs lg:text-sm text-score-low" />
        </Field.Root>
      </div>
    </Fieldset.Root>
  );
};
